/**
 * PlanStep - Diagnostic testing/workup plan based on selected diagnoses
 *
 * Step 3 in the ChartMind flow:
 * 1. Record - Transcribe encounter
 * 2. Diagnosis - Select differential diagnoses
 * 3. Plan - Choose tests to confirm/rule out
 * 4. Chart - Generate medical note
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Button,
} from '@mui/material';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import SelectableCard from './SelectableCard';
import AddTestModal from './AddTestModal';
import TestResultModal from './testResultTemplates/TestResultModal';
import Plan2CategoryTable, { Plan2ModeToggle } from './Plan2CategoryTable';
import Plan2HintsPanel from './Plan2HintsPanel';
import {
  PLAN2_TEST_CATEGORY_KEYS,
  PLAN2_TEST_TOGGLE_LABELS,
  applyPlan2FocusFlip,
  buildPlan2Hints,
  createEmptyPlan2ByCategory,
  ensurePlan2Defaults,
  fillPlan2Normals,
  fillPlan2ReassuringFromPending,
  getPlan2BulkActionLabel,
  markPlan2PendingFlip,
  normalizePlan2Values,
  usesMarkAllReassuring,
} from './plan2Catalog';

// ============================================================================
// Types
// ============================================================================

export interface Diagnosis {
  condition: string;
  likelihood: string;
  rationale?: string;
  urgent?: boolean;
}

export interface DiagnosticTest {
  id: string;
  name: string;
  category: 'lab' | 'imaging' | 'procedure' | 'other';
  rationale: string;
  linkedDiagnoses: string[];
  // stat/urgent/routine from ChartMind's prompt; high/medium/low from ChartMind V2's.
  priority: 'stat' | 'urgent' | 'routine' | 'high' | 'medium' | 'low';
  turnaroundTime?: string;
  estimatedCost?: string;
  isCustom?: boolean;
}

export interface DiagnosticPlanData {
  tests: DiagnosticTest[];
  testingStrategy?: string;
  considerations?: string[];
}

export interface PlanStepProps {
  selectedDiagnoses: Diagnosis[];
  planData: DiagnosticPlanData | null;
  loading: boolean;
  error: string | null;
  hasTests: boolean;
  retry: () => void;
  disabledTestIds: Set<string>;
  onToggleTestDisabled: (testId: string) => void;
  onAddTest: (test: Partial<DiagnosticTest>) => void;
  testResults: Map<string, any>;
  onUpdateTestResult: (testId: string, result: any) => void;
  /** Plan2 table mode state by diagnostic category. */
  plan2ByCategory?: Record<
    string,
    { mode?: "list" | "table"; values?: Record<string, string[]>; pendingFlip?: Record<string, boolean> }
  >;
  onPlan2ModeChange?: (categoryKey: string, mode: "list" | "table", itemIds?: string[]) => void;
  onPlan2ItemValueChange?: (categoryKey: string, itemId: string, value: string[]) => void;
  onPlan2ItemFocus?: (categoryKey: string, itemId: string) => void;
  onPlan2ItemBlur?: (categoryKey: string, itemId: string) => void;
  onPlan2DocumentNormal?: (categoryKey: string, itemIds?: string[]) => void;
  /**
   * Page-level Clinical Hints (Management). When defined, hides per-section hint cards and
   * follows this open state under *2 rows.
   */
  clinicalHintsOpen?: boolean;
  /**
   * Admission/Progress: lock every category to the *2 table, hide list|*2 toggles, and label
   * sections without the " 2" suffix (Laboratory, Imaging, …).
   */
  plan2Only?: boolean;
  /** Heading. Defaults to ChartMind's "Diagnostic Plan"; ChartMind V2 calls the step "Tests". */
  title?: string;
  /** The line under the heading. */
  description?: string;
  /**
   * "page" = standalone h4 (default). "section" = subtitle under a parent page title
   * (Events note type Plan: Diagnostics under Event Management Approach).
   */
  headingLevel?: "page" | "section";
  /**
   * The result dialog as one list where exactly one answer is chosen, "Reassuring" included, and
   * nothing saves until Save. Off by default, which is ChartMind's dialog; ChartMind V2 turns it on.
   */
  singleChoiceResults?: boolean;
}

const DEFAULT_TITLE = 'Diagnostic Plan';
const DEFAULT_DESCRIPTION =
  "Review recommended tests to confirm or rule out your diagnoses. Use the edit button to enter results. Hide tests that don't apply.";

// ============================================================================
// Constants
// ============================================================================

const CATEGORY_LABELS: Record<string, string> = {
  lab: 'Laboratory',
  imaging: 'Imaging',
  procedure: 'Procedures',
  other: 'Other',
};

// ============================================================================
// Sub-Components
// ============================================================================

interface TestCardProps {
  test: DiagnosticTest;
  disabled: boolean;
  onToggleDisabled: () => void;
  onClick: () => void;
  testResult?: any;
}

const getResultSummary = (testResult: any): string | null => {
  if (!testResult) return null;
  if (typeof testResult === 'string') return testResult;
  if (testResult.prose) return testResult.prose;
  if (testResult.status === 'Normal') return 'Normal';
  if (testResult.status === 'Abnormal') return testResult.description || 'Abnormal - see details';
  return 'Result entered';
};

const TestCard: React.FC<TestCardProps> = ({ test, disabled, onToggleDisabled, onClick, testResult }) => {
  const chips: Array<{
    label: string;
    color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    variant?: 'filled' | 'outlined';
  }> = [];

  if (test.isCustom) chips.push({ label: 'Custom', color: 'default', variant: 'outlined' });
  if (test.priority === 'stat') chips.push({ label: 'STAT', color: 'error', variant: 'filled' });
  else if (test.priority === 'urgent') chips.push({ label: 'Urgent', color: 'warning', variant: 'filled' });
  else if (test.priority === 'high') chips.push({ label: 'High priority', color: 'warning', variant: 'outlined' });

  const resultSummary = getResultSummary(testResult);

  const resultContent = resultSummary ? (
    <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
        Result:
      </Typography>
      <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
        {resultSummary}
      </Typography>
    </Box>
  ) : null;

  return (
    <SelectableCard
      title={test.name}
      rationale={test.rationale}
      selectionVariant="none"
      disabled={disabled}
      onEdit={onClick}
      editTooltip={testResult ? "Edit result" : "Enter result"}
      onToggleDisabled={onToggleDisabled}
      hideTooltip="Exclude"
      restoreTooltip="Restore"
      chips={chips}
      additionalContent={resultContent}
    />
  );
};

// ============================================================================
// Main Component
// ============================================================================

const PlanStep: React.FC<PlanStepProps> = ({
  selectedDiagnoses,
  planData,
  loading,
  error,
  hasTests,
  retry,
  disabledTestIds,
  onToggleTestDisabled,
  onAddTest,
  testResults,
  onUpdateTestResult,
  plan2ByCategory,
  onPlan2ModeChange,
  onPlan2ItemValueChange,
  onPlan2ItemFocus,
  onPlan2ItemBlur,
  onPlan2DocumentNormal,
  clinicalHintsOpen,
  plan2Only = false,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  headingLevel = "page",
  singleChoiceResults = false,
}) => {
  const titleVariant = headingLevel === "section" ? "h5" : "h4";
  // ============================================================================
  // TODO: RE-ENABLE VISUALIZATION AFTER FIXING BUGS
  // Temporarily disabled visualization tab due to bugs - needs fixing before deadline
  // Uncomment below to restore visualization functionality:
  // ============================================================================
  // const [viewMode, setViewMode] = useState(1); // 0 = visualization, 1 = list
  // const visualizationContainerRef = useRef<HTMLDivElement>(null);
  // const [vizDimensions, setVizDimensions] = useState({ width: 600, height: 400 });
  
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState<DiagnosticTest | null>(null);
  /** Local Plan2 state so X|X 2 works even if session handlers are missing after HMR. */
  const [localPlan2ByCategory, setLocalPlan2ByCategory] = useState(() =>
    createEmptyPlan2ByCategory(),
  );
  const [localHintsOpen, setLocalHintsOpen] = useState(false);
  const hintsControlled = clinicalHintsOpen !== undefined;
  const tipsOpen = hintsControlled ? Boolean(clinicalHintsOpen) : localHintsOpen;
  const plan2Controlled = typeof onPlan2ModeChange === 'function';
  const effectivePlan2 = plan2Controlled
    ? plan2ByCategory || createEmptyPlan2ByCategory()
    : localPlan2ByCategory;

  const updateLocalPlan2 = (categoryKey: string, updater: (current: any) => any) => {
    setLocalPlan2ByCategory((prev) => {
      const current = prev[categoryKey] || createEmptyPlan2ByCategory()[categoryKey];
      return { ...prev, [categoryKey]: updater(current) };
    });
  };

  const changePlan2Mode = (category: string, mode: 'list' | 'table', itemIds: string[]) => {
    if (plan2Controlled) {
      onPlan2ModeChange?.(category, mode, itemIds);
      return;
    }
    updateLocalPlan2(category, (current) => {
      let next = { ...current, mode };
      if (mode === 'table') next = ensurePlan2Defaults(next, itemIds);
      return next;
    });
  };

  const changePlan2Value = (category: string, itemId: string, value: string[]) => {
    if (plan2Controlled) {
      onPlan2ItemValueChange?.(category, itemId, value);
      return;
    }
    updateLocalPlan2(category, (current) => ({
      ...current,
      values: { ...(current.values || {}), [itemId]: normalizePlan2Values(value) },
    }));
  };

  const focusPlan2Item = (category: string, itemId: string) => {
    if (plan2Controlled) {
      onPlan2ItemFocus?.(category, itemId);
      return;
    }
    updateLocalPlan2(category, (current) => applyPlan2FocusFlip(current, itemId).state);
  };

  const blurPlan2Item = (category: string, itemId: string) => {
    if (plan2Controlled) {
      onPlan2ItemBlur?.(category, itemId);
      return;
    }
    updateLocalPlan2(category, (current) => markPlan2PendingFlip(current, itemId));
  };

  const documentPlan2Normal = (category: string, itemIds: string[]) => {
    if (plan2Controlled) {
      onPlan2DocumentNormal?.(category, itemIds);
      return;
    }
    updateLocalPlan2(category, (current) =>
      usesMarkAllReassuring(category)
        ? fillPlan2ReassuringFromPending(current, itemIds)
        : fillPlan2Normals(current, itemIds),
    );
  };

  // Handlers for test result modal
  const handleTestClick = (test: DiagnosticTest) => {
    setSelectedTest(test);
    setResultModalOpen(true);
  };

  const handleResultModalClose = () => {
    setResultModalOpen(false);
    setSelectedTest(null);
  };

  const handleResultSave = (result: any) => {
    if (selectedTest) {
      onUpdateTestResult(selectedTest.id, result);
      handleResultModalClose();
    }
  };

  // Group tests by category, including disabled (they render in-place, just dimmed)
  const testsByCategory = useMemo(() => {
    if (!planData?.tests) return new Map<string, DiagnosticTest[]>();

    const grouped = new Map<string, DiagnosticTest[]>();
    for (const test of planData.tests) {
      const category = test.category || 'other';
      if (!grouped.has(category)) grouped.set(category, []);
      grouped.get(category)!.push(test);
    }
    return grouped;
  }, [planData?.tests]);

  // Seed Ordered, pending when *2 table mode is already active (default) and tests appear.
  useEffect(() => {
    Array.from(testsByCategory.entries()).forEach(([category, tests]) => {
      if (!tests.length) return;
      const itemIds = tests.map((test) => test.id);
      const state = effectivePlan2?.[category];
      if (state?.mode !== 'table') return;
      const missing = itemIds.some(
        (id) => !Object.prototype.hasOwnProperty.call(state.values || {}, id),
      );
      if (!missing) return;
      changePlan2Mode(category, 'table', itemIds);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testsByCategory, effectivePlan2]);

  // Get existing test names for duplicate check
  const existingTestNames = planData?.tests?.map((t) => t.name) || [];

  // ============================================================================
  // TODO: RE-ENABLE VISUALIZATION AFTER FIXING BUGS
  // ============================================================================
  // React.useEffect(() => {
  //   if (viewMode === 0 && visualizationContainerRef.current) {
  //     const observer = new ResizeObserver((entries) => {
  //       if (entries[0]) {
  //         const { width } = entries[0].contentRect;
  //         setVizDimensions({ width, height: 400 });
  //       }
  //     });
  //     observer.observe(visualizationContainerRef.current);
  //     return () => observer.disconnect();
  //   }
  // }, [viewMode]);

  // ============================================================================
  // TODO: RE-ENABLE VISUALIZATION AFTER FIXING BUGS
  // ============================================================================
  // const testsForVisualization = useMemo(() => {
  //   if (!planData?.tests) return [];
  //   return planData.tests.map((test) => ({
  //     condition: test.name,
  //     likelihood: 'Recommended',
  //     rationale: test.rationale,
  //     urgent: false,
  //   }));
  // }, [planData?.tests]);

  // No diagnoses selected
  if (selectedDiagnoses.length === 0) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant={titleVariant} sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Select a diagnosis first to generate a diagnostic workup plan.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ textAlign: headingLevel === "section" ? "left" : "center", mb: 4 }}>
        <Typography variant={titleVariant} sx={{ mb: 1.5 }}>
          {title}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {description}
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ width: '100%', mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={retry} startIcon={<RefreshRounded />}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Generating diagnostic workup...
          </Typography>
        </Box>
      )}

      {/* Tests */}
      {!loading && hasTests && planData && (
        <Box sx={{ width: '100%' }}>
          <Box display="flex" justifyContent="flex-end" mb={3}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddRounded />}
              onClick={() => setAddModalOpen(true)}
            >
              Add Test
            </Button>
          </Box>

          {/* ============================================================================ */}
          {/* TODO: RE-ENABLE VISUALIZATION AFTER FIXING BUGS */}
          {/* ============================================================================ */}
          {/* {viewMode === 0 && (
            <Box ref={visualizationContainerRef} sx={{ mb: 3 }}>
              <ChartMindVisualization
                diagnoses={testsForVisualization}
                selectedIds={planData.tests.filter(t => !disabledTestIds.has(t.id)).map(t => t.name)}
                onDiagnosisClick={(test) => {
                  const testObj = planData.tests.find(t => t.name === test.condition);
                  if (testObj) {
                    onToggleTestDisabled(testObj.id);
                  }
                }}
                onAddDiagnosis={() => setAddModalOpen(true)}
                width={vizDimensions.width}
                height={vizDimensions.height}
              />
            </Box>
          )} */}

          {/* Testing Strategy — info alert so it reads as guidance, not another selectable card */}
          {planData.testingStrategy && (
            <Alert severity="info" sx={{ width: '100%', mb: 3 }}>
              <AlertTitle>Testing Strategy</AlertTitle>
              <Typography variant="body2" color="text.secondary" component="div">
                {planData.testingStrategy}
              </Typography>
            </Alert>
          )}

          {/* List View - Now always visible */}

          {!hintsControlled ? (
            <Plan2HintsPanel
              open={tipsOpen}
              onToggle={() => setLocalHintsOpen((prev) => !prev)}
              hasHints={buildPlan2Hints(planData.tests || []).length > 0}
            />
          ) : null}

          {/* Tests by category — Exam-style X|X 2 toggle, then list or table */}
          {Array.from(testsByCategory.entries()).map(([category, tests]) => {
            const label = CATEGORY_LABELS[category] || 'Other';
            const toggleLabels =
              PLAN2_TEST_TOGGLE_LABELS[category as keyof typeof PLAN2_TEST_TOGGLE_LABELS];
            const supportsPlan2 =
              Boolean(toggleLabels) &&
              PLAN2_TEST_CATEGORY_KEYS.includes(
                category as (typeof PLAN2_TEST_CATEGORY_KEYS)[number],
              );
            const categoryState = effectivePlan2?.[category];
            const mode =
              supportsPlan2 && (plan2Only || categoryState?.mode === 'table')
                ? 'table'
                : 'list';
            const itemIds = tests.map((test) => test.id);
            const tipsByItemId = Object.fromEntries(
              buildPlan2Hints(tests).map((hint) => [hint.itemId, [hint.line]]),
            );

            return (
              <Box key={category} sx={{ mb: 3 }}>
                {supportsPlan2 ? (
                  plan2Only ? (
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                      {toggleLabels.list}
                    </Typography>
                  ) : (
                    <Plan2ModeToggle
                      listLabel={toggleLabels.list}
                      tableLabel={toggleLabels.table}
                      mode={mode}
                      onModeChange={(nextMode) =>
                        changePlan2Mode(category, nextMode as 'list' | 'table', itemIds)
                      }
                    />
                  )
                ) : (
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {label}
                  </Typography>
                )}
                {mode === 'table' ? (
                  <Plan2CategoryTable
                    items={tests.map((test) => ({ id: test.id, name: test.name }))}
                    values={categoryState?.values || {}}
                    pendingFlip={categoryState?.pendingFlip || {}}
                    variant="diagnostic"
                    tipsOpen={tipsOpen}
                    tipsByItemId={tipsByItemId}
                    onChangeItemValue={(itemId, value) =>
                      changePlan2Value(category, itemId, value)
                    }
                    onItemFocus={(itemId) => focusPlan2Item(category, itemId)}
                    onItemBlur={(itemId) => blurPlan2Item(category, itemId)}
                    onDocumentNormal={() => documentPlan2Normal(category, itemIds)}
                    documentNormalLabel={getPlan2BulkActionLabel(category)}
                    extraOptionsById={Object.fromEntries(
                      tests.map((test) => {
                        const result = testResults.get(test.id);
                        const extras: string[] = [];
                        if (typeof result === 'string' && result.trim()) {
                          extras.push(result.trim());
                        } else if (result?.prose) {
                          extras.push(String(result.prose).trim());
                        } else if (result?.status === 'Normal') extras.push('Normal');
                        else if (result?.status === 'Abnormal' && result.description) {
                          extras.push(String(result.description).trim());
                        }
                        return [test.id, extras.filter(Boolean)];
                      }),
                    )}
                  />
                ) : (
                  tests.map((test) => (
                    <TestCard
                      key={test.id}
                      test={test}
                      disabled={disabledTestIds.has(test.id)}
                      onToggleDisabled={() => onToggleTestDisabled(test.id)}
                      onClick={() => handleTestClick(test)}
                      testResult={testResults.get(test.id)}
                    />
                  ))
                )}
              </Box>
            );
          })}

          {/* Considerations */}
          {planData.considerations && planData.considerations.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Clinical Considerations
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
                    {planData.considerations.map((item, idx) => (
                      <li key={idx}>
                        <Typography variant="body2">{item}</Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
          )}
        </Box>
      )}

      {/* Empty state — still offer Add Test so a missing/failed generation is not a dead end. */}
      {!loading && !hasTests && !error && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            No diagnostic tests generated yet. Add your own, or retry.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddRounded />}
            onClick={() => setAddModalOpen(true)}
          >
            Add Test
          </Button>
        </Box>
      )}

      {/* Add Test Modal */}
      <AddTestModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={onAddTest}
        existingTests={existingTestNames}
      />

      {/* Test Result Modal */}
      <TestResultModal
        open={resultModalOpen}
        test={selectedTest}
        onSave={handleResultSave}
        onCancel={handleResultModalClose}
        existingResult={selectedTest ? testResults.get(selectedTest.id) : null}
        singleChoice={singleChoiceResults}
        existingTestNames={existingTestNames}
        onAddReflexTest={(reflexTest) => {
          onAddTest({
            name: reflexTest.name,
            category: reflexTest.category as any,
            rationale: reflexTest.rationale,
            priority: reflexTest.priority as 'stat' | 'urgent' | 'routine',
            id: `${reflexTest.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`,
            linkedDiagnoses: [],
          });
        }}
      />
    </Box>
  );
};

export default PlanStep;
